import { useCallback, useEffect, useRef, useState } from 'react'

import { uebersetze, type Ereignis } from '@/lib/api'

type Fertig = Extract<Ereignis, { typ: 'fertig' }>

export interface Lauf {
  dialekt: string
  zustand: 'laeuft' | 'fertig' | 'fehler'
  text: string
  quelle: 'live' | 'cache' | null
  ergebnis: Fertig | null
  meldung: string | null
}

export interface Uebersetzung {
  laeuft: boolean
  lauf: Lauf | null
  /** Reststand aus der letzten Antwort — `null`, solange keine vorliegt. */
  rest: number | null
  starte: (text: string, dialekt: string) => void
  abbrechen: () => void
}

export function useUebersetzung(): Uebersetzung {
  const [laeuft, setLaeuft] = useState(false)
  const [lauf, setLauf] = useState<Lauf | null>(null)
  const [rest, setRest] = useState<number | null>(null)
  const steuerung = useRef<AbortController | null>(null)

  const abbrechen = useCallback(() => {
    steuerung.current?.abort()
    steuerung.current = null
    setLaeuft(false)
  }, [])

  useEffect(() => abbrechen, [abbrechen])

  const starte = useCallback(
    (text: string, dialekt: string) => {
      abbrechen()
      const controller = new AbortController()
      steuerung.current = controller

      setLaeuft(true)
      setLauf({
        dialekt,
        zustand: 'laeuft',
        text: '',
        quelle: null,
        ergebnis: null,
        meldung: null,
      })

      void (async () => {
        try {
          for await (const ereignis of uebersetze(text, dialekt, controller.signal)) {
            if (controller.signal.aborted) return
            switch (ereignis.typ) {
              case 'start':
                setLauf((bisher) => (bisher ? { ...bisher, quelle: ereignis.quelle } : bisher))
                break
              case 'text':
                setLauf((bisher) =>
                  bisher ? { ...bisher, text: bisher.text + ereignis.text } : bisher,
                )
                break
              case 'fertig':
                setLauf((bisher) =>
                  bisher
                    ? {
                        ...bisher,
                        zustand: 'fertig',
                        text: ereignis.dialekt_text,
                        ergebnis: ereignis,
                      }
                    : bisher,
                )
                setRest(ereignis.kontingent_rest)
                break
              case 'fehler':
                setLauf((bisher) =>
                  bisher ? { ...bisher, zustand: 'fehler', meldung: ereignis.meldung } : bisher,
                )
                if (ereignis.kontingent_rest !== undefined) setRest(ereignis.kontingent_rest)
                break
            }
          }
        } catch (ausnahme) {
          if (controller.signal.aborted) return
          setLauf((bisher) =>
            bisher
              ? {
                  ...bisher,
                  zustand: 'fehler',
                  meldung:
                    ausnahme instanceof TypeError
                      ? 'Keine Verbindung zum Server.'
                      : 'Da ist etwas schiefgegangen. Probier es nochmal.',
                }
              : bisher,
          )
        } finally {
          if (steuerung.current === controller) {
            steuerung.current = null
            setLaeuft(false)
          }
        }
      })()
    },
    [abbrechen],
  )

  return { laeuft, lauf, rest, starte, abbrechen }
}
